import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, TextInputProps } from 'react-native';
import { useSearchStore } from 'stores/searchStore';
import { isWeb } from 'utils/deviceUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import {
  CloseWrapper,
  IconWrapper,
  searchInputStyles,
  Wrapper,
} from './styles';

type SearchInputProps = {
  testID: string;
  small?: boolean;
  clearable?: boolean;
  isTesting?: boolean;
  isSheet?: boolean;
  marginBottom?: number;
  onHandlePress?: () => void;
  onHandleFocus?: () => void;
  accessibilityLabel?: string;
} & TextInputProps;

const SearchInput = ({
  testID,
  value,
  placeholder,
  small = true,
  clearable = false,
  isTesting = false,
  isSheet = false,
  marginBottom,
  onChangeText,
  onHandlePress,
  onHandleFocus,
  accessibilityLabel,
  ...props
}: SearchInputProps) => {
  const inputRef = useRef<TextInput | any>(null);
  const { searchInputActive, setSearchInputActive } = useSearchStore(
    (state) => state
  );

  const CustomInput =
    isWeb || isTesting || !isSheet ? TextInput : BottomSheetTextInput;
  const { t } = useTranslation();
  useEffect(() => {
    if (!searchInputActive) {
      inputRef.current?.blur();
    }
  }, [searchInputActive]);

  const handleChangeText = (changedText: string) => {
    onChangeText?.(changedText);
  };

  const handlePress = () => {
    inputRef.current?.focus();
    setSearchInputActive(true);
    onHandlePress?.();
  };

  const handleFocus = () => {
    setSearchInputActive(true);
    onHandleFocus?.();
  };

  const clearInput = () => {
    handleChangeText('');
    inputRef.current?.blur();
  };

  return (
    <Wrapper
      onPress={handlePress}
      {...testIdentifiers(testID)}
      accessible={false}
      small={small}
      marginBottom={marginBottom}>
      <IconWrapper accessible={false}>
        <Icon
          name="search"
          size="s"
          color={theme.colors.gray300}
          testID={`${testID}-icon`}
        />
      </IconWrapper>
      <CustomInput
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        placeholderTextColor={theme.colors.gray300}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        {...testIdentifiers(`${testID}-input`)}
        style={searchInputStyles.input}
        accessible={true}
        accessibilityRole="search"
        accessibilityLabel={
          accessibilityLabel ?? placeholder ?? t('search.searchField')
        }
        accessibilityHint={t('ariaLabels.searchHint')}
        importantForAccessibility="yes"
        accessibilityElementsHidden={false}
        accessibilityState={{ disabled: false }}
        textContentType="none"
        {...props}
      />
      {clearable && value !== '' && (
        <CloseWrapper
          onPress={clearInput}
          testID={`${testID}-clear`}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={t('ariaLabels.clearSearch', 'Clear search')}
          aria-label={t('ariaLabels.clearSearch')}
          importantForAccessibility="yes"
          accessibilityElementsHidden={false}
          small={small}>
          <Icon
            name="close"
            size="s"
            color={theme.colors.gray300}
            testID={`${testID}-close-icon`}
          />
        </CloseWrapper>
      )}
    </Wrapper>
  );
};

export default SearchInput;
